/**
 * Row and icon metrics shared by the workspace rail and every panel beside
 * it (Chats, Apps, Scheduled, My Drive, Work Chats). They follow ChatGPT's
 * sidebar: 36px rows, 10px side padding, 8px between icon and label, 8px
 * radius, 14/20 light-regular labels in the sidebar row tone, and 20px thin
 * outline icons. Rows sit flush; sections are separated by their own 36px
 * heading rows, not by extra margins.
 */

/** Height, padding, icon gap and radius of a panel row. */
export const SIDEBAR_ROW_CLASS = 'h-9 gap-2 rounded-md px-2.5';

/**
 * Row label type: 14/20 at a light-regular 360. Geist's 400 at 14px in the
 * near-black row tone reads as bold in a dense list; 360 reads as plain text.
 */
export const SIDEBAR_ROW_LABEL_CLASS = 'text-sm font-[360]';

/**
 * Resting colour of a row label (and the panel title): the
 * `--sidebar-row-foreground` token, which matches ChatGPT's sidebar text in
 * each theme (near-black at 85% in light, 85% of the foreground in dark).
 * The shared navigation state applies the same tone to inactive rows, so
 * labels inside those rows inherit it; active rows switch to the full
 * foreground. Use this only on rows that do not take the navigation state.
 * Section headings use `text-sidebar-section-foreground`.
 */
export const SIDEBAR_ROW_TEXT_CLASS = 'text-sidebar-row-foreground';

/** Label colour of the active (current page) row. */
export const SIDEBAR_ROW_ACTIVE_TEXT_CLASS = 'text-sidebar-accent-foreground';

/**
 * Lucide stroke for every rail and panel icon. Sized by measured ink, not by
 * box: ChatGPT's rail/panel icons put ~74–102px² of ink in a 20px box (New
 * chat 84); stock Lucide at 20px/1.75 put ~100–143px² and read visibly
 * bigger, and a 19px box still measured ~100–140px² (New chat 110). A 17px
 * box with a 1.5 stroke (≈1.06px rendered) lands New chat at ~88px² and the
 * rail at ~80px², inside ChatGPT's range.
 */
export const SIDEBAR_ICON_STROKE_WIDTH = 1.5;

/**
 * Every icon in the rail and the panel chrome: rail destinations, panel and
 * section header actions, row leading icons and row trailing actions. A 17px
 * box with the thin stroke (a CSS stroke-width outranks Lucide's
 * `stroke-width` attribute, and the paths inherit it). It carries its own
 * size so Button's default `size-4` for unsized icons never applies.
 */
export const SIDEBAR_ICON_CLASS = 'size-[17px] shrink-0 [stroke-width:1.5]';

/** A row's leading icon (or the box an icon is centred in). */
export const SIDEBAR_ROW_ICON_CLASS = SIDEBAR_ICON_CLASS;

/** A rail button: 36px, ChatGPT's 10px radius. */
export const SIDEBAR_RAIL_BUTTON_CLASS =
  'relative size-9 rounded-[10px] transition-colors';

/**
 * Icon-only actions in the panel: panel header tools, section header actions
 * and row trailing buttons (⋯, new chat in project, pin). One 32px square,
 * which fits inside the 36px rows and headings without touching their edges.
 */
export const SIDEBAR_ACTION_BUTTON_CLASS =
  'text-sidebar-icon-foreground hover:text-sidebar-accent-foreground size-8 shrink-0 rounded-lg';

/** An inactive rail destination's icon tone (ChatGPT's mid grey). */
export const SIDEBAR_RAIL_INACTIVE_CLASS =
  'text-sidebar-icon-foreground hover:text-sidebar-accent-foreground';

/**
 * The hover-revealed slot a row's trailing action button sits in: collapsed
 * to nothing at rest, one action button wide on hover, focus or while its
 * menu is open.
 */
export const SIDEBAR_ROW_ACTION_SLOT_OPEN_CLASS = 'w-8 opacity-100';
export const SIDEBAR_ROW_ACTION_SLOT_HOVER_CLASS =
  'w-0 opacity-0 group-focus-within:w-8 group-focus-within:opacity-100 group-hover:w-8 group-hover:opacity-100';

/**
 * Space between a section heading and its first row: 4px (ChatGPT's heading
 * centre sits 40px above the first row's centre). A collapsed section (its
 * disclosure toggle reports `aria-expanded="false"`) drops it, so a heading
 * with no rows under it adds nothing to the gap before the next section.
 * Menu and popover triggers in the heading also report `aria-expanded`, but
 * carry `aria-haspopup`, so they are left out.
 */
export const SIDEBAR_SECTION_HEADER_GAP_CLASS =
  'mb-1 has-[[aria-expanded=false]:not([aria-haspopup])]:mb-0';

/**
 * A section heading row. Its label starts on the same 10px axis as the row
 * labels below it; pair it with `SIDEBAR_SECTION_LABEL_CLASS` for the type.
 * It carries the 4px heading-to-rows gap.
 */
export const SIDEBAR_SECTION_HEADER_ROW_CLASS = `flex h-9 items-center px-2.5 ${SIDEBAR_SECTION_HEADER_GAP_CLASS}`;

/**
 * Space above a section that follows another section: ChatGPT leaves 20px
 * between a section's last row and the next heading (rows inside a section
 * stay flush on their 36px pitch).
 */
export const SIDEBAR_SECTION_GAP_CLASS = 'mt-5';

/**
 * Space above the first section under a panel's top rows (New chat; My
 * Drive and Media): 8px.
 */
export const SIDEBAR_FIRST_SECTION_GAP_CLASS = 'mt-2';

/**
 * Section spacing for a container whose direct children include whole
 * sections, each marked `data-sidebar-section`, under the panel's top rows.
 * The first section sits 8px below those rows and every later one 20px below
 * the previous section's last row. Spacing hangs off the sections that
 * render, so a section that renders nothing leaves no gap behind.
 */
export const SIDEBAR_SECTIONS_UNDER_ROWS_CLASS =
  '[&>[data-sidebar-section]]:mt-2 [&>[data-sidebar-section]~[data-sidebar-section]]:mt-5';

/**
 * The same for a panel whose first section opens the panel (Scheduled, Work
 * Chats): the first section keeps its place, later ones sit 20px below the
 * previous section's last row.
 */
export const SIDEBAR_SECTIONS_CLASS =
  '[&>[data-sidebar-section]~[data-sidebar-section]]:mt-5';

/** Date dividers, "Show more", empty notes: the 12/16 meta size. */
export const SIDEBAR_META_TEXT_CLASS = 'text-xs';
